import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_TYPES } from "@/lib/ui";
import { ShieldCheck, HeartHandshake, Clock, Sparkles, ArrowRight } from "lucide-react";
import HeroesBoard from "@/components/HeroesBoard";
import ImpactWall from "@/components/ImpactWall";

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/browse" : "/register";

  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-12 sm:pt-20 pb-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="k-fade-in">
          <span className="inline-flex items-center gap-2 bg-[#E8F4F1] text-[#2E7D6B] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <HeartHandshake className="w-4 h-4" /> A trusted human connection
          </span>
          <h1 className="text-4xl sm:text-6xl font-display font-bold text-slate-900 leading-[1.05] tracking-tight">
            Help is always <span className="text-[#D96B43]">a kind hand</span> away.
          </h1>
          <p className="text-lg sm:text-xl text-slate-700 leading-relaxed mt-6 max-w-xl">
            Kindred connects elders, people living alone, and anyone needing a hand with caring helpers — paid or
            voluntary — for errands, doctor visits, surgery transport and companionship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Link to={cta} data-testid="hero-get-help-btn"
              className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-8 min-h-[56px] rounded-xl shadow-md flex items-center justify-center gap-2 text-lg transition-colors">
              Find Help Now <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/register" data-testid="hero-become-helper-btn"
              className="border-2 border-[#E2D9CE] hover:border-[#2E7D6B] bg-white text-slate-800 font-semibold px-8 min-h-[56px] rounded-xl flex items-center justify-center text-lg transition-colors">
              Become a Helper
            </Link>
          </div>
          <div className="flex items-center gap-6 mt-8 text-slate-600">
            <span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#2E7D6B]" /> Verified helpers</span>
            <span className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#2E7D6B]" /> Fast matching</span>
          </div>
        </div>
        <div className="relative k-fade-in">
          <img src="/hero-errands.jpg"
            alt="A young neighbour carrying grocery bags for a smiling elderly woman" className="rounded-3xl shadow-xl w-full h-[420px] object-cover" />
          <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl shadow-lg border border-[#E2D9CE] px-5 py-4 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-[#FBEBE4] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[#D96B43]" />
            </span>
            <div>
              <p className="font-bold text-slate-900 leading-tight">AI Companion</p>
              <p className="text-sm text-slate-500">Guides you every step</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <h2 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">How Kindred can help you</h2>
        <p className="text-lg text-slate-600 mt-2">Choose the support you need. Some helpers volunteer their time, others charge a small fee.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {SERVICE_TYPES.map((s) => (
            <Link to="/browse" key={s.value} data-testid={`service-card-${s.value}`}
              className="bg-white border border-[#E2D9CE] rounded-2xl p-6 hover:shadow-md hover:border-[#D96B43] transition-all group">
              <span className="w-14 h-14 rounded-2xl bg-[#FBEBE4] flex items-center justify-center mb-4 group-hover:bg-[#D96B43] transition-colors">
                <s.icon className="w-7 h-7 text-[#D96B43] group-hover:text-white transition-colors" />
              </span>
              <h3 className="text-xl font-semibold text-slate-900">{s.label}</h3>
              <p className="text-slate-600 mt-1">Find caring, trusted people ready to help.</p>
            </Link>
          ))}
        </div>
      </section>

      <ImpactWall />

      <HeroesBoard />

      {/* Steps */}
      <section className="bg-[#2E7D6B] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <h2 className="text-2xl sm:text-3xl font-display font-bold">Getting help is simple</h2>
          <div className="grid sm:grid-cols-3 gap-8 mt-10">
            {[
              ["1", "Post your request", "Tell us what you need and when. Mark it as paid or voluntary."],
              ["2", "Match with a helper", "A verified, caring helper accepts and connects with you."],
              ["3", "Get it done", "Chat, receive help, and leave a kind review afterwards."],
            ].map(([n, t, d]) => (
              <div key={n} className="bg-white/10 rounded-2xl p-6">
                <span className="w-12 h-12 rounded-full bg-white text-[#2E7D6B] font-display font-bold text-xl flex items-center justify-center">{n}</span>
                <h3 className="text-xl font-semibold mt-4">{t}</h3>
                <p className="text-white/85 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <Link to={cta} className="inline-flex items-center gap-2 bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-8 min-h-[56px] rounded-xl mt-10 text-lg transition-colors">
            Get started <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-4 sm:px-8 py-10 text-slate-500">
        <p className="font-display text-xl font-bold text-slate-800">Kindred</p>
        <p className="mt-1">A trusted human connection — built for the September 2026 Decoding Data Science Agentic Challenge.</p>
      </footer>
    </div>
  );
}
